import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';

// Animations
const pulseGreen = keyframes`
  0% { opacity: 0.6; box-shadow: 0 0 5px rgba(74, 222, 128, 0.2); }
  50% { opacity: 1; box-shadow: 0 0 15px rgba(74, 222, 128, 0.6); }
  100% { opacity: 0.6; box-shadow: 0 0 5px rgba(74, 222, 128, 0.2); }
`;

const pulseRed = keyframes`
  0% { opacity: 0.6; box-shadow: 0 0 5px rgba(239, 68, 68, 0.2); }
  50% { opacity: 1; box-shadow: 0 0 15px rgba(239, 68, 68, 0.6); }
  100% { opacity: 0.6; box-shadow: 0 0 5px rgba(239, 68, 68, 0.2); }
`;

const VelocityBar = ({ label, value, maxRange = 0.005 }) => {
    // Value is % decimal (e.g. 0.0012 for 0.12%)
    // maxRange is the scale cap (e.g. 0.5% = 0.005)

    const absValue = Math.abs(value);
    const percentage = Math.min((absValue / maxRange) * 100, 100); // 0 to 100% width
    const isPositive = value >= 0;
    const color = isPositive ? '#4ade80' : '#f87171'; // Green / Red

    // Animate if high velocity (> 0.15% change)
    const isHighVelocity = absValue > 0.0015;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, height: '24px' }}>
            {/* Label */}
            <Typography variant="caption" sx={{ width: '40px', color: 'text.secondary', fontWeight: 700, fontFamily: 'monospace' }}>
                {label}
            </Typography>

            {/* Bar Container */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                height: '100%',
                mx: 1,
                borderLeft: '1px solid rgba(255,255,255,0.1)',
                borderRight: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.02)',
            }}>
                {/* Center Line */}
                <Box sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    background: 'rgba(255,255,255,0.2)'
                }} />

                {/* The Bar */}
                <Box sx={{
                    height: '8px',
                    position: 'absolute',
                    left: '50%', // Start from center
                    width: `${percentage / 2}%`, // Divide by 2 because container is full width, bar takes half at most
                    // If positive, origin is left (growing right). If negative, origin is right (growing left)
                    transformOrigin: isPositive ? 'left' : 'right',
                    transform: isPositive ? 'scaleX(1)' : 'translateX(-100%) scaleX(1)',
                    background: isPositive
                        ? `linear-gradient(90deg, rgba(74, 222, 128, 0.4) 0%, rgba(74, 222, 128, 1) 100%)`
                        : `linear-gradient(270deg, rgba(239, 68, 68, 0.4) 0%, rgba(239, 68, 68, 1) 100%)`,
                    borderRadius: '1px',
                    boxShadow: `0 0 10px ${color}80`,
                    animation: isHighVelocity
                        ? `${isPositive ? pulseGreen : pulseRed} 1.5s infinite`
                        : 'none'
                }} />
            </Box>

            {/* Value */}
            <Typography variant="caption" sx={{
                width: '55px',
                textAlign: 'right',
                color: color,
                fontWeight: 700,
                fontFamily: 'monospace'
            }}>
                {isPositive ? '+' : ''}{(value * 100).toFixed(2)}%
            </Typography>
        </Box>
    );
};

const MomentumCluster = ({ data }) => {
    if (!data) return null;

    // Check Multi-Timeframe Continuity (all green or all red)
    const allGreen = data['5m'] > 0 && data['15m'] > 0 && data['1h'] > 0;
    const allRed = data['5m'] < 0 && data['15m'] < 0 && data['1h'] < 0;

    let glowColor = 'transparent';
    if (allGreen) glowColor = 'rgba(74, 222, 128, 0.1)';
    if (allRed) glowColor = 'rgba(239, 68, 68, 0.1)';

    return (
        <React.Fragment>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon sx={{ fontSize: '1rem', color: '#00d4aa' }} />
                Temporal Momentum
            </Typography>

            <Box sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(30, 41, 59, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                mb: 3,
                boxShadow: allGreen || allRed ? `0 0 30px ${glowColor}` : 'none',
                borderLeft: allGreen ? '2px solid #4ade80' : allRed ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.5s ease'
            }}>
                <VelocityBar label="5m" value={data['5m']} maxRange={0.003} />
                <VelocityBar label="15m" value={data['15m']} maxRange={0.006} />
                <VelocityBar label="1h" value={data['1h']} maxRange={0.012} />

                {allGreen && (
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#4ade80', fontWeight: 900, letterSpacing: '0.1em', animation: `${pulseGreen} 2s infinite` }}>
                        BULLISH CONTINUITY
                    </Typography>
                )}
                {allRed && (
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#ef4444', fontWeight: 900, letterSpacing: '0.1em', animation: `${pulseRed} 2s infinite` }}>
                        BEARISH CONTINUITY
                    </Typography>
                )}
            </Box>
        </React.Fragment>
    );
};

export default MomentumCluster;
